import { useState, useEffect } from "react";
import { businessApi } from "@/api/businessApi";
import { IBusiness } from "@/interfaces/Ibusiness";
import { FiX, FiGlobe, FiMail, FiPhone } from "react-icons/fi";
import { IoBusinessOutline } from "react-icons/io5";
import { FaMapMarkerAlt } from "react-icons/fa";
import BusinessHoursSelector from "@/components/BusinessHoursSelector";
import toast from "react-hot-toast";

interface EditBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessData: IBusiness | null;
  onUpdate: (updatedData: IBusiness) => void;
}

const EditBusinessModal = ({
  isOpen,
  onClose,
  businessData,
  onUpdate,
}: EditBusinessModalProps) => {
  const [formData, setFormData] = useState<IBusiness>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<"profile" | "hours">("profile");

  useEffect(() => {
    if (businessData) {
      setFormData({
        business_name: businessData.business_name || "",
        timezone: businessData.timezone || "America/New_York",
        address: {
          line_1: businessData.address?.line_1 || "",
          line_2: businessData.address?.line_2 || "",
          city: businessData.address?.city || "",
          state: businessData.address?.state || "",
          zip_code: businessData.address?.zip_code || "",
          country: businessData.address?.country || "",
        },
        phones: businessData.phones || [""],
        emails: businessData.emails || [""],
        social_links: {
          website: businessData.social_links?.website || "",
          facebook: businessData.social_links?.facebook || "",
          twitter: businessData.social_links?.twitter || "",
          linkedin: businessData.social_links?.linkedin || "",
          instagram: businessData.social_links?.instagram || "",
          youtube: businessData.social_links?.youtube || "",
        },
        about: businessData.about || "",
        working_hours: businessData.working_hours || {
          mo: null,
          tu: null,
          we: null,
          th: null,
          fr: null,
          sa: null,
          su: null,
        },
      });
    }
  }, [businessData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAddressChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const handleSocialLinksChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [field]: value,
      },
    }));
  };

  const handleArrayChange = (
    field: "phones" | "emails",
    index: number,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field]?.map((item, i) => (i === index ? value : item)) || [
        value,
      ],
    }));
  };

  const addArrayItem = (field: "phones" | "emails") => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), ""],
    }));
  };

  const removeArrayItem = (field: "phones" | "emails", index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field]?.filter((_, i) => i !== index) || [],
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.business_name?.trim()) {
      newErrors.business_name = "Business name is required";
    }

    if (!formData.address?.city?.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.address?.state?.trim()) {
      newErrors.state = "State is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    try {
      const dataToSend = {
        ...formData,
        working_hours: formData.working_hours || {
          mo: null,
          tu: null,
          we: null,
          th: null,
          fr: null,
          sa: null,
          su: null,
        },
      };
      console.log("Sending to API - timezone:", dataToSend.timezone);
      console.log("Full dataToSend:", dataToSend);
      const response = await businessApi.updateBusinessData(dataToSend);
      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error("Error updating business data:", error);
      setErrors({
        submit: "Failed to update business data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto flex">
        {/* Sidebar Tabs */}
        <div className="w-48 border-r flex flex-col py-8 px-4 bg-gray-50">
          {/* Avatar and Business Name */}
          <div className="mb-8 flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-3xl text-gray-400 mb-2">
              {formData.business_name?.charAt(0) || <IoBusinessOutline />}
            </div>
            <div className="font-semibold text-center text-gray-700 text-base">
              {formData.business_name || "Business Name"}
            </div>
          </div>
          <button
            className={`mb-2 px-3 py-2 rounded-lg text-left ${
              activeTab === "profile"
                ? "bg-white text-[#78C7D1] font-semibold"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("profile")}
          >
            Profile
          </button>
          <button
            className={`px-3 py-2 rounded-lg text-left ${
              activeTab === "hours"
                ? "bg-white text-[#78C7D1] font-semibold"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("hours")}
          >
            Business hours
          </button>
        </div>
        {/* Main Content */}
        <div className="flex-1 p-8 overflow-y-auto relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiX className="h-5 w-5" />
          </button>
          {/* margin top 40px */}
          <div className="mt-7">
            {activeTab === "profile" && (
              <form
                className="space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                {/* Business Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <div className="flex items-center space-x-3">
                    <IoBusinessOutline className="text-gray-400" />
                    <input
                      type="text"
                      value={formData.business_name || ""}
                      onChange={(e) =>
                        handleInputChange("business_name", e.target.value)
                      }
                      className={`flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1] ${
                        errors.business_name
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Enter business name"
                    />
                  </div>
                  {errors.business_name && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.business_name}
                    </p>
                  )}
                </div>
                {/* Address */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Address</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address Line 1
                    </label>
                    <div className="flex items-center space-x-3">
                      <FaMapMarkerAlt className="text-gray-400" />
                      <input
                        type="text"
                        value={formData.address?.line_1 || ""}
                        onChange={(e) =>
                          handleAddressChange("line_1", e.target.value)
                        }
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                        placeholder="Street address"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={formData.address?.line_2 || ""}
                      onChange={(e) =>
                        handleAddressChange("line_2", e.target.value)
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                      placeholder="Apartment, suite, etc."
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        value={formData.address?.city || ""}
                        onChange={(e) =>
                          handleAddressChange("city", e.target.value)
                        }
                        className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1] ${
                          errors.city ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="City"
                      />
                      {errors.city && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.city}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        value={formData.address?.state || ""}
                        onChange={(e) =>
                          handleAddressChange("state", e.target.value)
                        }
                        className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1] ${
                          errors.state ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="State"
                      />
                      {errors.state && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.state}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        value={formData.address?.zip_code || ""}
                        onChange={(e) =>
                          handleAddressChange("zip_code", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                        placeholder="ZIP Code"
                      />
                    </div>
                  </div>
                </div>
                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Contact Information
                  </h3>
                  {/* Phone Numbers */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Numbers
                    </label>
                    <div className="space-y-2">
                      {formData.phones?.map((phone, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2"
                        >
                          <FiPhone className="text-gray-400" />
                          <input
                            type="tel"
                            value={phone || ""}
                            onChange={(e) =>
                              handleArrayChange("phones", index, e.target.value)
                            }
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                            placeholder="Phone number"
                          />
                          {formData.phones && formData.phones.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeArrayItem("phones", index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                            >
                              <FiX className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayItem("phones")}
                        className="text-[#78C7D1] hover:text-[#5ba3ad] text-sm font-medium"
                      >
                        + Add Phone Number
                      </button>
                    </div>
                  </div>
                  {/* Email Addresses */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Addresses
                    </label>
                    <div className="space-y-2">
                      {formData.emails?.map((email, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2"
                        >
                          <FiMail className="text-gray-400" />
                          <input
                            type="email"
                            value={email || ""}
                            onChange={(e) =>
                              handleArrayChange("emails", index, e.target.value)
                            }
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                            placeholder="Email address"
                          />
                          {formData.emails && formData.emails.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeArrayItem("emails", index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                            >
                              <FiX className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayItem("emails")}
                        className="text-[#78C7D1] hover:text-[#5ba3ad] text-sm font-medium"
                      >
                        + Add Email Address
                      </button>
                    </div>
                  </div>
                </div>
                {/* Social Links */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Social Links
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website
                    </label>
                    <div className="flex items-center space-x-3">
                      <FiGlobe className="text-gray-400" />
                      <input
                        type="url"
                        value={formData.social_links?.website || ""}
                        onChange={(e) =>
                          handleSocialLinksChange("website", e.target.value)
                        }
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Facebook
                      </label>
                      <input
                        type="url"
                        value={formData.social_links?.facebook || ""}
                        onChange={(e) =>
                          handleSocialLinksChange("facebook", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                        placeholder="Facebook URL"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Twitter
                      </label>
                      <input
                        type="url"
                        value={formData.social_links?.twitter || ""}
                        onChange={(e) =>
                          handleSocialLinksChange("twitter", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                        placeholder="Twitter URL"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        LinkedIn
                      </label>
                      <input
                        type="url"
                        value={formData.social_links?.linkedin || ""}
                        onChange={(e) =>
                          handleSocialLinksChange("linkedin", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                        placeholder="LinkedIn URL"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Instagram
                      </label>
                      <input
                        type="url"
                        value={formData.social_links?.instagram || ""}
                        onChange={(e) =>
                          handleSocialLinksChange("instagram", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                        placeholder="Instagram URL"
                      />
                    </div>
                  </div>
                </div>
                {/* About */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    About
                  </label>
                  <textarea
                    value={formData.about || ""}
                    onChange={(e) => handleInputChange("about", e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                    placeholder="Tell customers about your business..."
                  />
                </div>
                {errors.submit && (
                  <p className="text-red-500 text-sm mb-2">{errors.submit}</p>
                )}
              </form>
            )}
            {activeTab === "hours" && (
              <BusinessHoursSelector
                initialWorkingHours={formData.working_hours ?? undefined}
                initialTimezone={formData.timezone ?? "UTC"}
                onSave={async (wh, tz) => {
                  console.log(
                    "BusinessHoursSelector onSave called with timezone:",
                    tz
                  );
                  try {
                    setIsLoading(true);
                    // Save working hours and timezone to API immediately
                    const dataToSave = {
                      working_hours: wh,
                      timezone: tz,
                    };
                    console.log("Saving working hours to API:", dataToSave);
                    await businessApi.updateBusinessData(dataToSave);
                    // Update local state after successful save
                    setFormData((prev) => ({
                      ...prev,
                      working_hours: wh,
                      timezone: tz,
                    }));
                    console.log("Working hours saved successfully");
                    toast.success("Business hours saved successfully");
                  } catch (error) {
                    console.error("Error saving working hours:", error);
                    toast.error(
                      "Failed to save business hours. Please try again."
                    );
                    throw error; // Re-throw to let BusinessHoursSelector handle the error
                  } finally {
                    setIsLoading(false);
                  }
                }}
                onTimezoneChange={(tz) => {
                  console.log("Timezone changed to:", tz);
                  setFormData((prev) => ({
                    ...prev,
                    timezone: tz,
                  }));
                }}
                isLoading={isLoading}
                showSaveCancelButtons={false}
              />
            )}
            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 pt-4 border-t mt-8">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isLoading}
                onClick={handleSubmit}
                className="px-4 py-2 bg-[#78C7D1] hover:bg-[#5ba3ad] text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBusinessModal;
